import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Bot, User, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";

const API = process.env.REACT_APP_BACKEND_URL;

function renderMd(text) {
  if (!text) return text;
  const raw = text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^##\s*#?\s*(.+)$/gm, '<strong class="block mt-1">$1</strong>')
    .replace(/\n/g, '<br/>');
  return DOMPurify.sanitize(raw, { ALLOWED_TAGS: ["strong", "em", "br"], ALLOWED_ATTR: ["class"] });
}

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `guest_${Date.now().toString(36)}`);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const scrollBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollBottom(); }, [messages, scrollBottom]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const resp = await fetch(`${API}/api/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (!resp.ok) throw new Error("Chat failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assistant = "";
      let buffer = "";
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") break;
            try {
              const parsed = JSON.parse(data);
              assistant += parsed.t;
            } catch {
              assistant += data;
            }
            setMessages(prev => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: assistant };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "Entschuldigung, es gab einen Fehler. Bitte versuche es erneut." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="concierge-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-5 py-3 shadow-xl transition-all duration-300 hover:scale-105"
        style={{
          background: "linear-gradient(135deg, hsl(155, 32%, 16%), hsl(155, 28%, 24%))",
          color: "#fff",
        }}
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
        {!open && <span className="text-sm font-medium hidden sm:inline">Fragen?</span>}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-50 flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-[hsl(40,20%,88%)]"
          style={{ width: "min(380px, calc(100vw - 2rem))", height: "min(520px, calc(100vh - 8rem))", background: "#FDFCFA" }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-5 py-4 shrink-0"
            style={{ background: "linear-gradient(135deg, hsl(155, 32%, 16%), hsl(155, 28%, 24%))" }}
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/15">
              <Bot size={20} className="text-white" />
            </div>
            <div>
              <div className="text-white font-semibold text-sm">Chalet Concierge</div>
              <div className="text-white/60 text-xs">Immer fuer dich da</div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ scrollbarWidth: "thin" }}>
            {messages.length === 0 && (
              <div className="text-center text-sm text-[hsl(155,10%,55%)] py-8 px-2">
                <Bot size={32} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium text-[hsl(155,20%,30%)]">Willkommen!</p>
                <p className="mt-1">Frag mich alles ueber das Chalet, die Umgebung oder deine Buchung.</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={`${msg.role}-${i}`} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: "hsl(155, 32%, 16%)" }}>
                    <Bot size={14} className="text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[hsl(155,32%,16%)] text-white rounded-br-md"
                      : "bg-[hsl(40,30%,94%)] text-[hsl(155,20%,18%)] rounded-bl-md"
                  }`}
                >
                  {msg.content ? (
                    <span dangerouslySetInnerHTML={{ __html: renderMd(msg.content) }} />
                  ) : (loading && i === messages.length - 1 ? (
                    <Loader2 size={16} className="animate-spin opacity-50" />
                  ) : "")}
                </div>
                {msg.role === "user" && (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-[hsl(30,35%,80%)]">
                    <User size={14} className="text-[hsl(30,30%,30%)]" />
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-[hsl(40,20%,90%)] bg-white px-3 py-3 flex gap-2">
            <input
              ref={inputRef}
              data-testid="concierge-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Schreib deine Frage..."
              className="flex-1 text-sm bg-[hsl(40,25%,97%)] rounded-xl px-4 py-2.5 outline-none focus:ring-2 ring-[hsl(155,25%,40%)]/30 placeholder:text-[hsl(40,10%,60%)]"
              disabled={loading}
            />
            <button
              data-testid="concierge-send"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="rounded-xl p-2.5 transition-all disabled:opacity-30"
              style={{ background: "hsl(155, 32%, 16%)", color: "#fff" }}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
